/**
 * The ONE destructive confirmation mechanism for web.
 *
 * `window.confirm` and hand-rolled dialogs are forbidden. Any call site awaits
 * `confirmDestructive()`; `ConfirmDestructiveHost` (mounted once at the app
 * root) renders what this raises.
 *
 * The second step is opt-in. Account deletion sets `typeToConfirm`. The
 * under-13 refusal path keeps its single confirmation and must omit that field.
 *
 * A second confirm raised while one is open replaces it. There is no queue.
 */

export type typeTypeToConfirmSpec = {
  /** Lowercase noun phrase naming the field, e.g. "username". */
  label: string;
  /** The exact string the fan has to type back. Shown on screen. Never empty. */
  value: string;
};

export type typeConfirmDestructiveInput = {
  /** Names the thing being destroyed, ending in a question mark. */
  title: string;
  /** Names everything this action destroys. May contain newlines. */
  body: string;
  /** The real verb, said out loud. Never "OK" and never "Yes". */
  confirmLabel: string;
  /** Defaults to "Cancel". */
  cancelLabel?: string | undefined;
  /**
   * Opt-in second step. Omit it and this is the ordinary one-step confirm.
   * Account deletion sets it; the under-13 refusal path must not.
   */
  typeToConfirm?: typeTypeToConfirmSpec | undefined;
};

export type typeConfirmDestructiveRequest = typeConfirmDestructiveInput & {
  resolve: (isConfirmed: boolean) => void;
};

export const CONFIRM_DESTRUCTIVE_CONTINUE_LABEL = "Continue";
export const CONFIRM_DESTRUCTIVE_DEFAULT_CANCEL_LABEL = "Cancel";

const listeners = new Set<(request: typeConfirmDestructiveRequest) => void>();

export function subscribeToConfirmDestructive(
  listener: (request: typeConfirmDestructiveRequest) => void,
): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function resolveStepOneConfirmLabel(
  input: typeConfirmDestructiveInput,
): string {
  if (input.typeToConfirm) return CONFIRM_DESTRUCTIVE_CONTINUE_LABEL;
  return input.confirmLabel;
}

export function confirmDestructive(
  input: typeConfirmDestructiveInput,
): Promise<boolean> {
  if (!input.title.trim() || !input.body.trim() || !input.confirmLabel.trim()) {
    throw new Error(
      "A destructive confirm needs a title, a body, and a confirm label",
    );
  }

  if (
    input.typeToConfirm &&
    (!input.typeToConfirm.label.trim() || !input.typeToConfirm.value.trim())
  ) {
    throw new Error("A type-to-confirm step needs a label and a value");
  }

  if (listeners.size === 0) return Promise.resolve(false);

  return new Promise<boolean>((resolve) => {
    for (const listener of listeners) listener({ ...input, resolve });
  });
}
